import React from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";

const SECTIONS = [
    {
        h: "1. Quick summary",
        p: [
            "We collect the bare minimum needed to run Castline. We never sell your personal data. Creators' video content is only shared with the Brands they accept. We use a small, well-known set of vendors (listed below) to actually deliver the Service.",
        ],
    },
    {
        h: "2. Who is the data controller",
        p: [
            "The team that builds and operates Castline (\"Castline\", \"we\", \"us\"), reachable at <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a>.",
        ],
    },
    {
        h: "3. What we collect",
        p: [
            "<strong>Account info</strong>: name, email address, role (creator / brand / admin), hashed password.",
            "<strong>Profile info</strong>: avatar, bio, payout destination (Stripe ID, PayPal email or bank-account label) — never raw card numbers, which Stripe handles directly.",
            "<strong>Content</strong>: videos you upload, titles, descriptions, thumbnails.",
            "<strong>Activity</strong>: invites sent, commissions accepted, payouts funded, ad-data entries logged. Server logs (IP address, user-agent, timestamps) used for security and debugging.",
            "<strong>Communications</strong>: messages you exchange with brands or creators inside Castline; emails you send us.",
        ],
    },
    {
        h: "4. How we use it",
        p: [
            "To operate the Service: invites, dashboards, attribution, payouts, emails.",
            "To improve the Service: aggregated, anonymised analytics.",
            "To prevent fraud and abuse, comply with the law, and respond to support requests.",
        ],
    },
    {
        h: "5. Who we share it with (sub-processors)",
        p: [
            "<strong>Stripe</strong> — payment processing.",
            "<strong>Resend</strong> — transactional email delivery (invites, welcome, payout notifications).",
            "<strong>MongoDB Atlas</strong> — primary database.",
            "<strong>Cloud storage provider</strong> — to host uploaded videos.",
            "<strong>Google Workspace</strong> — for our internal email and support.",
            "We share only what each vendor needs and require them to handle data per their published security and privacy policies.",
        ],
    },
    {
        h: "6. Data location & transfers",
        p: [
            "Data is stored on servers located in the United States. If you access Castline from elsewhere, you agree to this transfer.",
        ],
    },
    {
        h: "7. How long we keep it",
        p: [
            "Active accounts: as long as your account is open.",
            "Closed accounts: deleted within 30 days, except records we're required to keep for tax, accounting or fraud-prevention reasons (typically up to 7 years).",
            "Server logs: 90 days.",
        ],
    },
    {
        h: "8. Your rights",
        p: [
            "You can request access, correction, export or deletion of your personal data at any time by emailing <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a>.",
            "If you're in the EU/UK, you have additional rights under GDPR/UK-GDPR (right to object, restrict processing, lodge a complaint with your local supervisory authority). Castline's lawful basis for processing is contract performance and legitimate interest in operating the Service.",
            "If you're in California, you have rights under the CCPA, including the right to know what we collect, the right to delete, and the right to opt out of sale (we don't sell personal data).",
        ],
    },
    {
        h: "9. Cookies & tracking",
        p: [
            "We use essential cookies to keep you signed in. We don't use third-party advertising cookies on joincastline.com. We don't sell your data to ad networks.",
        ],
    },
    {
        h: "10. Children",
        p: [
            "Castline isn't intended for users under 18. We don't knowingly collect data from children. If you believe a minor has signed up, contact us and we'll remove the account.",
        ],
    },
    {
        h: "11. Security",
        p: [
            "Passwords are stored as bcrypt hashes. Traffic is encrypted in transit (HTTPS). Payment info is handled by Stripe (PCI-DSS Level 1). We follow reasonable industry practices but no system is perfectly secure — please use a strong, unique password.",
        ],
    },
    {
        h: "12. Changes",
        p: [
            "We may update this Privacy Policy. If changes are material, we'll notify active users by email at least 14 days before they take effect.",
        ],
    },
    {
        h: "13. Contact",
        p: [
            "Reach us at <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a> for any privacy question or to exercise any of your rights.",
        ],
    },
];

export default function Privacy() {
    return (
        <div className="min-h-screen bg-white text-slate-900 legal-page">
            <header className="border-b hairline bg-white sticky top-0 z-40 print:hidden">
                <div className="max-w-[760px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-sm">c</div>
                        <span className="font-display text-lg font-semibold">castline</span>
                        <span className="text-slate-300 mx-1">·</span>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Privacy Policy</span>
                    </Link>
                    <button onClick={() => window.print()} className="btn-pill-ghost inline-flex items-center text-sm">
                        <Printer className="w-4 h-4 mr-2" /> PDF
                    </button>
                </div>
            </header>
            <main className="max-w-[760px] mx-auto px-5 lg:px-8 py-12 lg:py-16">
                <div className="chip chip-coral inline-flex mb-4">VERSION 1 · APRIL 2026</div>
                <h1 className="font-display text-4xl md:text-5xl font-semibold mb-3 leading-tight tracking-tight">
                    Privacy Policy
                </h1>
                <p className="text-sm text-slate-500 mb-10">
                    Effective date: 28 April 2026.
                </p>
                <div className="space-y-8">
                    {SECTIONS.map((s) => (
                        <section key={s.h} data-testid={`pp-${s.h.split(".")[0]}`}>
                            <h2 className="font-display text-xl font-semibold mb-3 text-slate-900">{s.h}</h2>
                            {s.p.map((para, i) => (
                                <p
                                    key={i}
                                    className="text-sm text-slate-700 leading-relaxed mb-3"
                                    dangerouslySetInnerHTML={{ __html: para }}
                                />
                            ))}
                        </section>
                    ))}
                </div>
                <footer className="border-t hairline mt-14 pt-6 text-xs text-slate-400 flex flex-wrap justify-between gap-3">
                    <div>Castline · joincastline.com</div>
                    <div>
                        <Link to="/terms" className="hover:text-coral">Terms</Link>
                        <span className="text-slate-300 mx-2">·</span>
                        <a href="mailto:hello@joincastline.com" className="hover:text-coral">hello@joincastline.com</a>
                    </div>
                </footer>
            </main>
        </div>
    );
}
